import { createContext, useCallback, useMemo, useState } from "react";

export interface AppProviderProps {
  children: React.ReactNode;
}

export interface AppState {
  submittalListFilter: { key: string; value: string } | null;
  materialListFilter: string | null;
  scheduleListFilter: string | null;
  bidPackageListFilter: string | null;
  uiVersion: number | null;
}

export const AppContext = createContext<any>({
  submittalListFilter: null,
  setSubmittalListFilter: () => {},
  materialListFilter: null,
  setMaterialListFilter: () => {},
  scheduleListFilter: null,
  setBidPackageListFilter: () => {},
  bidPackageListFilter: null,
  uiVersion: null,
  setUiVersion: () => {}
});

function AppProvider({ children }: AppProviderProps) {
  const [appState, setAppState] = useState<AppState>({
    submittalListFilter: null,
    materialListFilter: null,
    scheduleListFilter: null,
    bidPackageListFilter: null,
    uiVersion: null
  });

  const setSubmittalListFilter = useCallback(
    (filter: { key: string; value: string }) => {
      setAppState((prevState: AppState) => ({
        ...prevState,
        submittalListFilter: filter
      }));
    },
    []
  );

  const setMaterialListFilter = useCallback((filter: string) => {
    setAppState((prevState: AppState) => ({
      ...prevState,
      materialListFilter: filter
    }));
  }, []);

  const setBidPackageListFilter = useCallback((filter: string) => {
    setAppState((prevState: AppState) => ({
      ...prevState,
      bidPackageListFilter: filter
    }));
  }, []);

  const setUiVersion = useCallback((version: number) => {
    setAppState((prevState: AppState) => ({
      ...prevState,
      uiVersion: version
    }));
  }, []);

  const contextValue = useMemo(() => {
    return {
      submittalListFilter: appState.submittalListFilter,
      setSubmittalListFilter,
      materialListFilter: appState.materialListFilter,
      setMaterialListFilter,
      scheduleListFilter: appState.scheduleListFilter,
      setBidPackageListFilter,
      bidPackageListFilter: appState.bidPackageListFilter,
      uiVersion: appState.uiVersion,
      setUiVersion
    };
  }, [
    appState.submittalListFilter,
    appState.materialListFilter,
    appState.scheduleListFilter,
    appState.bidPackageListFilter,
    appState.uiVersion,
    setSubmittalListFilter,
    setMaterialListFilter,
    setBidPackageListFilter,
    setUiVersion
  ]);

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
}

export default AppProvider;
